import { useState, useEffect, useCallback, useRef } from "react";
import {
  getAdminReviewContext,
  getReviewDocuments,
  type ProgenyReviewDocuments,
} from "@/services/api/progeny";

/**
 * Hook for fetching review context and documents for the debug view.
 *
 * @param reviewId - The review ID to fetch data for
 * @returns Object containing context, documents, loading/error state, and a refresh function
 */
export function useProgenyReview(reviewId: string) {
  const [context, setContext] = useState<Record<string, unknown> | null>(null);
  const [documents, setDocuments] = useState<ProgenyReviewDocuments | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const mountedRef = useRef(true);

  const fetchData = useCallback(async () => {
    if (!reviewId) return;
    setLoading(true);
    setError(null);
    try {
      const [ctx, docs] = await Promise.all([
        getAdminReviewContext(reviewId).catch(() => null),
        getReviewDocuments(reviewId).catch(() => null),
      ]);
      if (mountedRef.current) {
        setContext(ctx);
        setDocuments(docs);
      }
    } catch (err: unknown) {
      if (mountedRef.current) {
        const message = err instanceof Error ? err.message : "Failed to fetch review data";
        setError(message);
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [reviewId]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();
    return () => { mountedRef.current = false; };
  }, [fetchData]);

  return { context, documents, loading, error, refresh: fetchData };
}
